var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import AddIcon from "@mui/icons-material/Add";
import GroupsIcon from "@mui/icons-material/Groups";
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Grid, Stack, Typography, } from "@mui/material";
import React from "react";
import { useNavigate } from "react-router-dom";
import { EmptyState } from "../common/EmptyState";
import { useDeleteDivision, useRetrieveDivisionsBySeasonId, } from "../api/DivisionApiProvider";
import { useRetrieveLeagueTeams } from "../api/LeagueApiProvider";
import { useAssignTeamsToDivisions } from "../api/SeasonApiProvider";
import { DivisionDropZone } from "../divisions/DivisionDropZone";
import { Loading } from "../nav/Loading";
import { DeleteDialog } from "../utils/DeleteDialog";
import { TeamDragger } from "./TeamDragger";
import { UnassignedTeamsDropZone } from "./UnassignedTeamsDropZone";
export var DivisionManagement = function (_a) {
    var _b;
    var season = _a.season, canModify = _a.canModify, onDeleteSuccess = _a.onDeleteSuccess, onDeleteError = _a.onDeleteError, onHasChangesChange = _a.onHasChangesChange, onSave = _a.onSave, _c = _a.showActions, showActions = _c === void 0 ? true : _c;
    var navigate = useNavigate();
    var _d = useRetrieveDivisionsBySeasonId({
        seasonId: season.id,
    }), divisions = _d.data, divisionsLoading = _d.isLoading, divisionsError = _d.error;
    var _e = useRetrieveLeagueTeams({
        leagueId: season.league,
        enabled: canModify,
    }), leagueTeams = _e.data, teamsLoading = _e.isLoading, teamsError = _e.error;
    // Local state to track division assignments before saving
    var _f = __read(React.useState({}), 2), localDivisionAssignments = _f[0], setLocalDivisionAssignments = _f[1];
    var _g = __read(React.useState(false), 2), hasChanges = _g[0], setHasChanges = _g[1];
    var _h = __read(React.useState(false), 2), isInitialized = _h[0], setIsInitialized = _h[1];
    var _j = __read(React.useState(false), 2), showDeleteDialog = _j[0], setShowDeleteDialog = _j[1];
    var _k = __read(React.useState(null), 2), divisionToDelete = _k[0], setDivisionToDelete = _k[1];
    var _l = __read(React.useState({ open: false, title: "", message: "", onConfirm: function () { } }), 2), confirmDialog = _l[0], setConfirmDialog = _l[1];
    // Initialize local assignments when divisions first load (only once)
    React.useEffect(function () {
        if (divisions && divisions.length > 0 && !isInitialized) {
            var initialAssignments_1 = {};
            divisions.forEach(function (division) {
                initialAssignments_1[division.id] = division.teams || [];
            });
            setLocalDivisionAssignments(initialAssignments_1);
            setIsInitialized(true);
        }
    }, [divisions, isInitialized]);
    // Notify parent when hasChanges changes
    React.useEffect(function () {
        onHasChangesChange === null || onHasChangesChange === void 0 ? void 0 : onHasChangesChange(hasChanges);
    }, [hasChanges, onHasChangesChange]);
    var assignTeamsMutation = useAssignTeamsToDivisions({
        seasonId: season.id,
        onSuccess: function () {
            onDeleteSuccess("Teams assigned successfully");
            setHasChanges(false);
            onSave === null || onSave === void 0 ? void 0 : onSave();
        },
        onError: function () {
            onDeleteError("Failed to assign teams");
        },
    });
    var deleteDivisionMutation = useDeleteDivision({
        onSuccess: function () { return onDeleteSuccess("Division deleted successfully"); },
        onError: function () {
            onDeleteError("Failed to delete division");
        },
    });
    var handleDeleteDivision = function (divisionId) {
        setDivisionToDelete(divisionId);
        setShowDeleteDialog(true);
    };
    var handleConfirmDelete = function () {
        if (divisionToDelete) {
            // Remove the division from local state before deletion to preserve other assignments
            setLocalDivisionAssignments(function (prev) {
                var updated = __assign({}, prev);
                delete updated[divisionToDelete];
                return updated;
            });
            deleteDivisionMutation.mutate({
                id: divisionToDelete,
                seasonId: season.id,
            });
        }
        setShowDeleteDialog(false);
        setDivisionToDelete(null);
    };
    var showConfirmDialog = function (title, message, onConfirm) {
        setConfirmDialog({ open: true, title: title, message: message, onConfirm: onConfirm });
    };
    var handleConfirmClose = function (confirmed) {
        if (confirmed) {
            confirmDialog.onConfirm();
        }
        setConfirmDialog({
            open: false,
            title: "",
            message: "",
            onConfirm: function () { },
        });
    };
    var handleEditDivision = function (divisionId) {
        if (hasChanges) {
            showConfirmDialog("Unsaved Changes", "You have unsaved changes. Are you sure you want to navigate away? Your changes will be lost.", function () {
                return navigate("/leagues/".concat(season.league, "/seasons/").concat(season.id, "/divisions/").concat(divisionId, "/edit"));
            });
            return;
        }
        navigate("/leagues/".concat(season.league, "/seasons/").concat(season.id, "/divisions/").concat(divisionId, "/edit"));
    };
    var handleTeamDrop = React.useCallback(function (team, monitor) {
        if (!isInitialized || !team.id) {
            return;
        }
        var dropResult = monitor.getDropResult();
        if (!dropResult) {
            return;
        }
        setLocalDivisionAssignments(function (prev) {
            var updated = __assign({}, prev);
            // Remove team from all divisions first
            Object.keys(updated).forEach(function (divId) {
                var numericDivId = parseInt(divId);
                if (updated[numericDivId]) {
                    updated[numericDivId] = updated[numericDivId].filter(function (t) { return t.id !== team.id; });
                }
            });
            // Add team to target division if not unassigning
            if (dropResult.divisionId && !dropResult.unassign) {
                if (!updated[dropResult.divisionId]) {
                    updated[dropResult.divisionId] = [];
                }
                var currentTeams = updated[dropResult.divisionId] || [];
                updated[dropResult.divisionId] = __spreadArray(__spreadArray([], __read(currentTeams), false), [team], false);
            }
            return updated;
        });
        setHasChanges(true);
    }, [isInitialized]);
    var handleSaveChanges = function () {
        var divisions = Object.entries(localDivisionAssignments).map(function (_a) {
            var _b = __read(_a, 2), divisionId = _b[0], teams = _b[1];
            return ({
                id: parseInt(divisionId),
                teamIds: teams.map(function (team) { return team.id; }),
            });
        });
        assignTeamsMutation.mutate({ divisions: divisions });
    };
    var handleResetChanges = React.useCallback(function () {
        if (divisions) {
            var initialAssignments_2 = {};
            divisions.forEach(function (division) {
                initialAssignments_2[division.id] = division.teams || [];
            });
            setLocalDivisionAssignments(initialAssignments_2);
            setHasChanges(false);
        }
    }, [divisions]);
    // Get unassigned teams (teams in league but not in any division)
    var getUnassignedTeams = function () {
        if (!leagueTeams)
            return [];
        var assignedTeamIds = new Set(Object.values(localDivisionAssignments).flatMap(function (teams) {
            return teams.map(function (team) { return team.id; }).filter(Boolean);
        }));
        return leagueTeams
            .filter(function (membership) { var _a; return membership.isActive && ((_a = membership.team) === null || _a === void 0 ? void 0 : _a.id); })
            .map(function (membership) { return membership.team; })
            .filter(function (team) { return !assignedTeamIds.has(team.id); })
            .sort(function (a, b) { return (a.name || "").localeCompare(b.name || ""); });
    };
    var unassignedTeams = getUnassignedTeams();
    return (_jsxs(Box, { children: [showActions && canModify && (_jsxs(Stack, { direction: "row", spacing: 1, justifyContent: "space-between", alignItems: "center", sx: { mb: 3 }, children: [_jsx(Typography, { variant: "body2", color: "text.secondary", children: "Drag and drop teams between divisions to organize your league." }), _jsxs(Stack, { direction: "row", spacing: 1, children: [hasChanges && (_jsxs(_Fragment, { children: [_jsx(Button, { variant: "outlined", size: "small", onClick: handleResetChanges, disabled: assignTeamsMutation.isLoading, children: "Reset" }), _jsx(Button, { variant: "contained", color: "primary", size: "small", onClick: handleSaveChanges, disabled: assignTeamsMutation.isLoading, children: "Save Changes" })] })), _jsx(Button, { variant: "outlined", size: "small", startIcon: _jsx(AddIcon, {}), onClick: function () {
                                    if (hasChanges) {
                                        showConfirmDialog("Unsaved Changes", "You have unsaved changes. Are you sure you want to navigate away? Your changes will be lost.", function () {
                                            return navigate("/leagues/".concat(season.league, "/seasons/").concat(season.id, "/divisions/create"));
                                        });
                                        return;
                                    }
                                    navigate("/leagues/".concat(season.league, "/seasons/").concat(season.id, "/divisions/create"));
                                }, children: "Add Division" })] })] })), (divisionsLoading || teamsLoading) && _jsx(Loading, {}), divisions && divisions.length === 0 && (_jsxs(Box, { sx: { textAlign: "center", py: 4 }, children: [_jsx(Typography, { color: "text.secondary", gutterBottom: true, children: "No divisions yet. Create your first division to start organizing teams." }), canModify && (_jsx(Button, { variant: "contained", startIcon: _jsx(AddIcon, {}), onClick: function () {
                            return navigate("/leagues/".concat(season.league, "/seasons/").concat(season.id, "/divisions/create"));
                        }, sx: { mt: 2 }, children: "Create First Division" }))] })), divisions && divisions.length > 0 && (_jsxs(Box, { sx: { display: "flex", gap: 2 }, children: [_jsx(Box, { sx: { flexGrow: 1 }, children: _jsx(Grid, { container: true, spacing: 2, children: divisions.map(function (division) { return (_jsx(Grid, { item: true, xs: 12, sm: 6, lg: 4, children: _jsx(DivisionDropZone, { division: division, onEditDivision: handleEditDivision, onDeleteDivision: handleDeleteDivision, canModify: canModify, teams: (localDivisionAssignments[division.id] || []).sort(function (a, b) {
                                        return (a.name || "").localeCompare(b.name || "");
                                    }), onTeamDrop: handleTeamDrop }) }, division.id)); }) }) }), canModify && (_jsx(Box, { sx: { width: 300, flexShrink: 0, alignSelf: "flex-start" }, children: _jsx(UnassignedTeamsDropZone, { season: season, onNavigate: function () {
                                var returnTo = "/leagues/".concat(season.league, "/seasons/").concat(season.id, "/?step=0");
                                var addTeamsUrl = "/leagues/".concat(season.league, "/teams/add/?returnTo=").concat(encodeURIComponent(returnTo));
                                if (hasChanges) {
                                    showConfirmDialog("Unsaved Changes", "You have unsaved changes. Are you sure you want to navigate away? Your changes will be lost.", function () { return navigate(addTeamsUrl); });
                                    return;
                                }
                                navigate(addTeamsUrl);
                            }, children: unassignedTeams.length === 0 ? (leagueTeams &&
                                leagueTeams.filter(function (m) { var _a; return m.isActive && ((_a = m.team) === null || _a === void 0 ? void 0 : _a.id); }).length >
                                    0 ? (_jsx(Typography, { variant: "body2", color: "text.secondary", children: "All teams are assigned to divisions" })) : (_jsx(EmptyState, { icon: _jsx(GroupsIcon, {}), heading: "No teams yet", description: "Add teams to this league to start assigning them to divisions.", actionLabel: "Add Teams", actionTo: "/leagues/".concat(season.league, "/teams/add/?returnTo=").concat(encodeURIComponent("/leagues/".concat(season.league, "/seasons/").concat(season.id, "/?step=0"))) }))) : (_jsx(Stack, { spacing: 1, children: unassignedTeams.map(function (team) { return (_jsx(TeamDragger, { team: team, onDrop: handleTeamDrop }, team.id)); }) })) }) }))] })), !!divisionsError && (_jsx(Alert, { severity: "error", sx: { mt: 2 }, children: "Failed to load divisions" })), !!teamsError && (_jsx(Alert, { severity: "error", sx: { mt: 2 }, children: "Failed to load teams" })), _jsx(DeleteDialog, { open: showDeleteDialog, title: "Delete ".concat(((_b = divisions === null || divisions === void 0 ? void 0 : divisions.find(function (d) { return d.id === divisionToDelete; })) === null || _b === void 0 ? void 0 : _b.name) || "Division", "?"), description: "This action cannot be undone and will remove all team assignments from this division.", onCancel: function () {
                    setShowDeleteDialog(false);
                    setDivisionToDelete(null);
                }, onConfirm: handleConfirmDelete }), _jsxs(Dialog, { open: confirmDialog.open, onClose: function () { return handleConfirmClose(false); }, "aria-labelledby": "confirm-dialog-title", "aria-describedby": "confirm-dialog-description", PaperProps: {
                    sx: {
                        borderLeft: 4,
                        borderColor: "warning.main",
                    },
                }, children: [_jsxs(DialogTitle, { id: "confirm-dialog-title", sx: {
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            color: "warning.main",
                            fontWeight: "bold",
                        }, children: ["\u26A0\uFE0F ", confirmDialog.title] }), _jsx(DialogContent, { children: _jsx(DialogContentText, { id: "confirm-dialog-description", sx: { color: "text.primary", fontWeight: 500 }, children: confirmDialog.message }) }), _jsxs(DialogActions, { children: [_jsx(Button, { onClick: function () { return handleConfirmClose(false); }, color: "primary", children: "Cancel" }), _jsx(Button, { onClick: function () { return handleConfirmClose(true); }, color: "warning", variant: "contained", sx: { fontWeight: "bold" }, children: "Continue" })] })] })] }));
};
